import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import HistoryIcon from "@mui/icons-material/History";
import { Title, usePermissions } from "react-admin";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import DateRangeFilter from "@/components/DateRangeFilter";
import PhoneLink from "@/components/PhoneLink";
import { callApi } from "@/dataprovider/misc-apis";
import { formatDisplayDateTime, formatIsoDate } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";

dayjs.extend(utc);

type RelayDashboardStats = {
  period?: { startDate: string; endDate: string };
  events?: {
    total?: number;
    active?: number;
    live?: number;
    gated?: number;
    default?: number;
  };
  accessRequests?: {
    submittedInPeriod?: number;
    audioOnlyInPeriod?: number;
  };
  liveEvents?: Array<{
    id: string;
    name: string;
    title: string | null;
    requiresAccessGrant: boolean;
    isDefault: boolean;
  }>;
  recentAccessRequests?: Array<{
    id: string;
    itsId: string;
    fullName: string | null;
    mobile: string | null;
    eventName: string;
    createdAt: string;
  }>;
};

type DashboardCardProps = {
  icon: ComponentType<SvgIconProps>;
  title: ReactNode;
  description: ReactNode;
  path: string;
  disabled?: boolean;
};

const navigateTo = (path: string) => {
  window.location.hash = `/${path}`;
  window.dispatchEvent(new Event("hashchange"));
};

const DashboardCard = ({ icon: Icon, title, description, path, disabled }: DashboardCardProps) => (
  <Card sx={{ minHeight: { xs: 120, sm: 140 }, width: "100%", opacity: disabled ? 0.55 : 1 }}>
    <CardActionArea
      onClick={() => !disabled && navigateTo(path)}
      disabled={disabled}
      sx={{ height: "100%" }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: { xs: "row", sm: "column" },
          alignItems: "center",
          gap: { xs: 1.5, sm: 0 },
          py: { xs: 2, sm: 2.5 },
        }}
      >
        <Icon sx={{ fontSize: { xs: 32, sm: 36 }, color: "primary.main", mb: { sm: 1 } }} />
        <Box sx={{ minWidth: 0, flex: 1, textAlign: { xs: "left", sm: "center" } }}>
          <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

function StatCard({
  title,
  value,
  subtitle,
  color = "primary.main",
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  color?: string;
}) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ color, mt: 0.5, fontSize: { xs: "1.35rem", sm: "1.5rem" } }}>
          {value}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

type RecentAccessRequestRow = NonNullable<RelayDashboardStats["recentAccessRequests"]>[number];

function RecentAccessRequestMobileList({ rows }: { rows: RecentAccessRequestRow[] }) {
  return (
    <Stack spacing={1}>
      {rows.map((row) => (
        <Paper
          key={row.id}
          variant="outlined"
          onClick={() => navigateTo(`relay-access-requests/${row.id}/show`)}
          sx={{
            p: 1.5,
            cursor: "pointer",
            borderColor: "divider",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>
            {row.itsId}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
            {row.fullName?.trim() || "—"}
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 0.25 }}>
            <PhoneLink phone={row.mobile} stopPropagation />
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            {row.eventName}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDisplayDateTime(row.createdAt, { empty: "—" })}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
}

function RecentAccessRequestTable({ rows }: { rows: RecentAccessRequestRow[] }) {
  return (
    <TableContainer sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <Table size="small" sx={{ minWidth: 640 }}>
        <TableHead>
          <TableRow>
            <TableCell>ITS ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Mobile</TableCell>
            <TableCell>Event</TableCell>
            <TableCell>Submitted</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => navigateTo(`relay-access-requests/${row.id}/show`)}
            >
              <TableCell>{row.itsId}</TableCell>
              <TableCell sx={{ wordBreak: "break-word" }}>{row.fullName?.trim() || "—"}</TableCell>
              <TableCell onClick={(event) => event.stopPropagation()}>
                <PhoneLink phone={row.mobile} stopPropagation />
              </TableCell>
              <TableCell sx={{ wordBreak: "break-word" }}>{row.eventName}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {formatDisplayDateTime(row.createdAt, { empty: "—" })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const formatEventLabel = (name: string) => name;

export default function RelayDashboard() {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const { permissions } = usePermissions();
  const canViewEvents = hasPermission(permissions, "relayEvents.view");
  const canViewRequests = hasPermission(permissions, "relayAccessRequests.view");

  const now = dayjs.utc();
  const defaultStartDate = formatIsoDate(now.startOf("month"));
  const defaultEndDate = formatIsoDate(now.endOf("month"));
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<RelayDashboardStats | null>(null);

  useEffect(() => {
    setLoading(true);
    void callApi({
      location: "relay/dashboard/stats",
      method: "GET",
      data: { startDate, endDate },
    })
      .then(({ data }) => {
        setStats((data as RelayDashboardStats) ?? null);
      })
      .catch((error: unknown) => {
        console.error("Failed to load relay dashboard stats:", error);
        setStats(null);
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  const events = stats?.events ?? {};
  const accessRequests = stats?.accessRequests ?? {};

  const recentRows = stats?.recentAccessRequests ?? [];

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, width: "100%", minWidth: 0, maxWidth: "100%" }}>
      <Title title="Relay" />
      <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
        Relay overview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Stream events, live status, and access grants
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DashboardCard
            icon={LiveTvIcon}
            title="Relay events"
            description="Configure streams, access gating, and live toggles"
            path="relay-events"
            disabled={!canViewEvents}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DashboardCard
            icon={HowToRegIcon}
            title="Access requests"
            description="Grant stream access for ITS users on gated events"
            path="relay-access-requests"
            disabled={!canViewRequests}
          />
        </Grid>
      </Grid>

      <DateRangeFilter
        defaultStartDate={defaultStartDate}
        defaultEndDate={defaultEndDate}
        onDateChange={(start, end) => {
          setStartDate(start || defaultStartDate);
          setEndDate(end || defaultEndDate);
        }}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 6, sm: 4, lg: 3 }}>
              <StatCard title="Live now" value={events.live ?? 0} color="success.main" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, lg: 3 }}>
              <StatCard title="Granted" value={accessRequests.submittedInPeriod ?? 0} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, lg: 3 }}>
              <StatCard title="Audio only" value={accessRequests.audioOnlyInPeriod ?? 0} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, lg: 3 }}>
              <StatCard
                title="Active events"
                value={events.active ?? 0}
                subtitle={`${events.total ?? 0} total`}
              />
            </Grid>
          </Grid>

          {(stats?.liveEvents?.length ?? 0) > 0 ? (
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <FiberManualRecordIcon sx={{ fontSize: 14, color: "success.main" }} />
                <Typography variant="subtitle1">Live streams</Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {stats?.liveEvents?.map((event) => (
                  <Chip
                    key={event.id}
                    label={formatEventLabel(event.title?.trim() || event.name)}
                    color="success"
                    variant="outlined"
                    onClick={
                      canViewEvents ? () => navigateTo(`relay-events/${event.id}`) : undefined
                    }
                    sx={{
                      cursor: canViewEvents ? "pointer" : "default",
                      maxWidth: "100%",
                      height: "auto",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        py: 0.75,
                      },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          ) : null}

          {canViewRequests ? (
            <>
              <Divider sx={{ my: { xs: 2, sm: 3 } }} />
              <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <HistoryIcon color="primary" />
                  <Typography variant="subtitle1">Recent access grants</Typography>
                </Box>
                {recentRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No access grants recorded yet.
                  </Typography>
                ) : isNarrow ? (
                  <RecentAccessRequestMobileList rows={recentRows} />
                ) : (
                  <RecentAccessRequestTable rows={recentRows} />
                )}
              </Paper>
            </>
          ) : null}
        </>
      )}
    </Box>
  );
}
